import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { lastValueFrom, map, take } from 'rxjs';
import { CountryModel } from 'src/app/api/models/country.model';
import { RiskItem } from 'src/app/api/models/quotationRequest.model';
import { CountryService } from 'src/app/api/services/country.service';
import { AppResource } from 'src/app/app.resource';
import { civiliteObject, Civility } from 'src/app/core/models/civility.model';
import { SaisieHabitation } from 'src/app/core/models/form-state.model';
import { State } from 'src/app/core/state/core.state';
import * as fromHousing from 'src/app/core/state/housing';
import * as fromLocataires from 'src/app/core/state/roommate';
import * as fromRoommate from 'src/app/core/state/roommate';
import { BaseComponent } from 'src/app/shared/components/base-component/base-component.component';
import { PAYMENT_PATH, ROOT_PATH } from 'src/app/shared/constants/route.constants';
import { Commune } from 'src/app/shared/models/components/geocodejson.model';
import { HeaderVM } from 'src/app/shared/models/components/headerVm';
import { QuestionVM } from 'src/app/shared/models/components/questionVm';
import { AdresseDataGouvService } from 'src/app/shared/services/adresse-data-gouv.service';

@Component({
  selector: 'app-coloc',
  templateUrl: './coloc.component.html',
  styleUrls: ['./coloc.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ColocComponent extends BaseComponent implements OnInit {
  headerStep!: HeaderVM;
  override question!: QuestionVM;

  civilities: Civility[] = civiliteObject;
  selectedCivility: Civility | undefined;

  cities = new Array<Commune>();
  filteredCity = new Array<Commune>();

  countries = new Array<CountryModel>();
  filteredCountries = new Array<CountryModel>();

  saisieHabitation!: SaisieHabitation;

  form: FormGroup;
  maxDate: Date;

  // TODO: IMPROVE FORM STRUCTURE
  constructor(
    store: Store<State>,
    resources: AppResource,
    private router: Router,
    private fb: FormBuilder,
    private countryService: CountryService,
    private adresseDataGouvService: AdresseDataGouvService
  ) {
    super(store, resources);

    this.headerStep = new HeaderVM(
      this.resource.header.stepContract,
      3,
      this.resource.header.subStepRoomMate,
      8
    );

    this.question = new QuestionVM(this.resource.question.roommate);

    this.form = this.fb.group({
      colocataires: this.fb.array([])
    });

    let today = new Date();
    today = new Date(today.setFullYear(today.getFullYear() - 18));
    this.maxDate = new Date(today.setDate(today.getDate() - 1));
  }

  override async ngOnInit() {
    this.saisieHabitation = await lastValueFrom(
      this.store.select(fromHousing.selectHousing).pipe(
        take(1),
        map(s => s.SaisieHabitation)
      )
    );

    await this.loadCountries();

    const locataireState = await lastValueFrom(
      this.store.select(fromLocataires.selectRoommate).pipe(take(1))
    );

    if (
      this.saisieHabitation.NombreColocataires != 0 &&
      this.saisieHabitation.NombreColocataires != undefined
    ) {
      for (let index = 0; index < this.saisieHabitation.NombreColocataires; index++) {
        this.addColoc(locataireState.roommate != null ? locataireState.roommate[index] : null);
      }
    } else {
      this.router.navigate([`${ROOT_PATH}${PAYMENT_PATH}`]);
    }
  }

  get colocataires(): FormArray {
    return this.form.get('colocataires') as FormArray;
  }

  addColoc(riskItem: RiskItem | null) {
    const colocForm = this.fb.group({
      civilite: [
        riskItem?.Fields['CIV_COLOC']
          ? this.civilities.find(obj => {
              return obj.value === riskItem?.Fields['CIV_COLOC'];
            })
          : '',
        Validators.required
      ],
      nom: [riskItem?.Fields['NOM_COLOC'] ?? '', Validators.required],
      prenom: [riskItem?.Fields['PRENOM_COLOC'] ?? '', Validators.required],
      dateNaissance: [riskItem?.Fields['DATENAISS_COLOC'] ?? '', Validators.required],
      codePostalNaissance: [
        riskItem?.Fields['CPNAISS_COLOC'] ?? '',
        [Validators.required, Validators.pattern('^[0-9]{5}$')]
      ],
      villeNaissance: [riskItem?.Fields['VILLENAISS_COLOC'] ?? '', Validators.required],
      paysNaissance: [
        riskItem?.Fields['PAYSNAISS_COLOC']
          ? this.countries.find(x => x.countryCode === riskItem?.Fields['PAYSNAISS_COLOC'])!
          : '',
        Validators.required
      ],
      nationalite: [
        riskItem?.Fields['NATIONALITE_COLOC']
          ? this.countries.find(x => x.countryCode === riskItem?.Fields['NATIONALITE_COLOC'])!
          : '',
        Validators.required
      ]
    });

    this.colocataires.push(colocForm);

    colocForm.get('codePostalNaissance')?.valueChanges.subscribe(postalCode => {
      if (postalCode?.length == 5) {
        this.adresseDataGouvService.getCityByPostalCode(postalCode).subscribe({
          next: (res: Commune[]) => {
            if (res) {
              this.cities = res;
              colocForm.get('villeNaissance')?.setValue(res[0].nomCommune);
            }
          }
        });
      } else {
        colocForm.get('villeNaissance')?.setValue('');
      }
    });

    colocForm.get('paysNaissance')!.valueChanges.subscribe((pays: string | CountryModel | null) => {
      if (
        pays &&
        typeof pays === 'object' &&
        pays !== null &&
        'countryNameFr' in pays &&
        (pays as CountryModel).countryNameFr === 'France'
      ) {
        colocForm.get('codePostalNaissance')!.setValidators([Validators.required]);
        colocForm.get('villeNaissance')!.setValidators([Validators.required]);
        colocForm.get('codePostalNaissance')!.updateValueAndValidity();
        colocForm.get('villeNaissance')!.updateValueAndValidity();
      } else {
        // MAKE inputs optional if not France
        colocForm.get('codePostalNaissance')!.setValidators(null);
        colocForm.get('villeNaissance')!.setValidators(null);
        colocForm.get('codePostalNaissance')!.updateValueAndValidity();
        colocForm.get('villeNaissance')!.updateValueAndValidity();
      }
    });
  }

  submit() {
    const riskItems: RiskItem[] = this.form.value.colocataires.map(
      (colocataire: any, index: number) => ({
        Order: index + 1,
        TypeCode: 'COL',
        Name: 'Colocataire',
        Fields: {
          CIV_COLOC: colocataire.civilite.text,
          NOM_COLOC: colocataire.nom,
          PRENOM_COLOC: colocataire.prenom,
          DATENAISS_COLOC: colocataire.dateNaissance,
          CPNAISS_COLOC: colocataire.codePostalNaissance,
          VILLENAISS_COLOC: colocataire.villeNaissance,
          PAYSNAISS_COLOC: colocataire.paysNaissance.countryCode,
          NATIONALITE_COLOC: colocataire.nationalite.countryCode
        }
      })
    );

    this.store.dispatch(fromRoommate.patchRoommate({ payload: riskItems }));
    this.router.navigate([`${ROOT_PATH}${PAYMENT_PATH}`]);
  }

  async loadCountries() {
    this.countries = await lastValueFrom(this.countryService.GetCountries().pipe(take(1)));

    // Sort countries by country code with France as first item of the list
    this.countries.sort((c1, c2) => {
      const franceCode = 'FR';
      if (c1.countryCode === franceCode) return -1;
      else if (c2.countryCode === franceCode) return 1;
      else return c1.countryCode.localeCompare(c2.countryCode); // Usual string comparison
    });

    this.filteredCountries = this.countries;
  }

  filterCity($event: any) {
    let query = $event.query;
    if (query != null) {
      var trimmedQuery = query
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLocaleLowerCase('fr-FR');
      this.filteredCity = this.cities.filter(item => {
        return (item.nomCommune ?? '')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLocaleLowerCase('fr-FR')
          .includes(trimmedQuery);
      });
    } else {
      this.filteredCity = this.cities;
    }
  }

  async filterCountries($event: any) {
    let query = $event.query;
    if (query != null) {
      let trimmedQuery = query
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLocaleLowerCase('fr-FR');
      this.filteredCountries = this.countries.filter(item => {
        return item.countryNameFr
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLocaleLowerCase('fr-FR')
          .includes(trimmedQuery);
      });
    } else {
      this.filteredCountries = this.countries;
    }
  }
}
